import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { CustomCart } from '@cart/const';
import { ActiveCartService } from '@cart/data-access';
import { PICKUP, PaymentMode, ValidationErrorKeys } from '@checkout/const';
import {
  CheckoutValidationService,
  PaymentModeService,
  PickupService,
  ReceiptService,
  TimeSlotsService,
} from '@checkout/feature';
import { BankCard, PaymentModeCodes } from '@payment/const';
import { PaymentService } from '@payment/feature';
import { ScrollToService } from '@shared/v2';
import { AuthService } from '@spartacus/core';
import { CurrentProfileService } from '@user/feature';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';

@Component({
  selector: 'sd-checkout-payment',
  templateUrl: './checkout-payment.component.html',
  styleUrls: ['./checkout-payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdvorCheckoutPaymentComponent implements OnInit, OnDestroy {
  isNotCompany$: Observable<boolean>;
  isEnable$: Observable<boolean>;
  isLoading$: Observable<boolean>;
  validationErrors$: Observable<string[] | undefined>;
  hasScheme$: Observable<boolean>;
  activeModeId$: Observable<string>;
  isOnlinePaymentAvailable$ = new BehaviorSubject<boolean>(false);
  paymentModes$: Observable<PaymentMode[] | undefined>;
  activeMode$: Observable<PaymentMode | undefined>;
  activeBankCard$: Observable<BankCard | undefined>;
  showManagerWillCall$: Observable<boolean>;

  paymentNowEnable$: Observable<boolean>;

  PaymentModeCodes = PaymentModeCodes;

  private subscription = new Subscription();

  constructor(
    private paymentModeService: PaymentModeService,
    private authService: AuthService,
    private paymentService: PaymentService,
    private currentProfileService: CurrentProfileService,
    private checkoutValidationService: CheckoutValidationService,
    private receiptService: ReceiptService,
    private pickupService: PickupService,
    private viewRef: ViewContainerRef,
    private scrollToService: ScrollToService,
    private activeCartService: ActiveCartService,
    private timeSlotsService: TimeSlotsService,
  ) {}

  ngOnInit(): void {
    this.activeModeId$ = this.receiptService.activeReceiptTypeId$;
    this.paymentModes$ = combineLatest({
      paymentModes: this.paymentModeService.getList(),
      isLoading: this.paymentModeService.getListLoading(),
      isError: this.paymentModeService.getListError(),
    }).pipe(
      tap(({ paymentModes, isLoading, isError }) => {
        if (!isLoading && !isError && paymentModes) {
          const isOnlinePaymentAvailable = paymentModes.some(
            (paymentMode) => paymentMode.code === 'sbrf' || paymentMode.code === 'sbp',
          );
          this.isOnlinePaymentAvailable$.next(isOnlinePaymentAvailable);
        }
      }),
      map(({ paymentModes, isLoading, isError }) => (isLoading || isError ? undefined : paymentModes)),
    );

    this.isNotCompany$ = this.currentProfileService.get().pipe(map((profile) => !profile.isLegal));

    this.showManagerWillCall$ = this.timeSlotsService.isEmpty();

    this.paymentNowEnable$ = this.activeCartService.getActive().pipe(
      map((cart: CustomCart) => {
        return !cart.isInflow;
      }),
    );

    this.isEnable$ = combineLatest({
      activePaymentCode: this.paymentModeService
        .getActivePaymentCode()
        .pipe(map((code) => code === PaymentModeCodes.Sbrf)),
      isUserLoggedIn: this.authService.isUserLoggedIn(),
    }).pipe(
      map((values) => Object.values(values).every((v) => v)),
      distinctUntilChanged(),
    );

    this.activeMode$ = combineLatest({
      modes: this.paymentModes$,
      code: this.paymentModeService.getActivePaymentCode(),
    }).pipe(
      filter(({ modes }) => Boolean(modes)),
      map(({ modes, code }) => {
        return (modes || []).find((mode: PaymentMode) => mode.code === code);
      }),
    );

    this.activeBankCard$ = this.paymentService.getActivePayment();

    this.isLoading$ = this.paymentService.getBindPaymentLoading();

    this.validationErrors$ = this.checkoutValidationService
      .getErrors(ValidationErrorKeys.PAYMENT_REQUIRED)
      .pipe(map((errors) => errors && Object.values(errors)));

    this.hasScheme$ = combineLatest({
      activeReceiptTypeId: this.receiptService.activeReceiptTypeId$,
      shop: this.pickupService.activeShop$,
    }).pipe(map(({ activeReceiptTypeId, shop }) => activeReceiptTypeId === PICKUP && !!shop?.scheme));

    this.subscription.add(
      this.checkoutValidationService.getScrollToEvent(ValidationErrorKeys.PAYMENT_REQUIRED).subscribe(() => {
        this.scrollToService.scrollToElement(this.viewRef.element.nativeElement);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
